import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, NavLink } from 'react-router-dom';
import { AppColors } from '../utils/utils';
import useToggle from '../hooks/useToggle';
import BookDemoFormModel from './models/BookDemoFormModel';

const GradientButton = styled(Button)(({ theme }: { theme: Theme }) => ({
  width: 'fit-content',
  height: 42,
  background: AppColors.aiGradientTransparent3,
  borderRadius: '12px',
  border: 0,
  color: 'white',
  padding: '10px 32px',
  minWidth: 0, // default for xs
  // Responsive override for md and up
  [theme.breakpoints.up('md')]: {
    minWidth: '170px',
  },
  textTransform: 'none',
  fontSize: 'clamp(0.8rem, 1.2vw, 1.2rem)',
  fontWeight: 500,
  boxShadow: 'none',
  textAlign: 'center',
  transition: '0.3s',
  '&:hover': {
    boxShadow: '0 6px 15px rgba(0, 0, 0, 0.3)',
    background: AppColors.aiGradient,
  },
}));

export const navItems = [
  { label: 'Features', path: '/features' },
  // { label: 'Pricing', path: '/pricing' },
  // { label: 'Videos', path: '/videos' },
];

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const {
    toggle: toggleBookDemoForm,
    onOpen: onOpenBookDemoForm,
    onClose: onCloseBookDemoForm,
  } = useToggle();

  const navigateToLogin = () => {
    const loginUrl = `https://knocard.app/login`;
    window.open(`${loginUrl}`) || window.location.replace(`${loginUrl}`);
  };

  return (
    <>
      <AppBar
        position='static'
        sx={{
          // background: AppColors.aiGradient,
          backgroundColor: 'white',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box
            component={Link}
            // to='/'
            to='https://knocard.com'
            target='_blank'
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <Box
              component='img'
              src='/static/images/knocard_ai_logo.png'
              alt='KnoCard Auto Logo'
              sx={{ height: { xs: 30, md: 40 } }}
            />
            <Divider
              orientation='vertical'
              sx={{
                display: { xs: 'none', md: 'flex' },
                background: '#e3e3e3',
                mx: 1,
                width: '1px',
                height: '32px',
              }}
            />
            <Typography
              variant='h6'
              fontWeight={'bold'}
              sx={{ color: '#9779ff' }}
            >
              AUTO
            </Typography>
          </Box>

          {/* Right side (Sign in + Mobile menu) */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Desktop Menu */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  component={NavLink}
                  to={item.path}
                  sx={{
                    color: AppColors.menuColor,
                    fontSize: 'clamp(0.8rem, 1.2vw, 1.2rem)',
                    textTransform: 'none',
                    '&.active': {
                      fontWeight: 'bold',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            <GradientButton
              variant='contained'
              sx={{
                ml: 2,
              }}
              onClick={navigateToLogin}
            >
              Sign In
            </GradientButton>

            <GradientButton
              variant='contained'
              onClick={() => {
                onOpenBookDemoForm();
              }}
              sx={{
                ml: 1,
              }}
            >
              Book Demo
            </GradientButton>

            {/* Mobile Menu Button */}
            <IconButton
              aria-label='menu'
              sx={{
                display: { xs: 'flex', md: 'none' },
                backgroundColor: 'white',
                color: 'black',
                borderRadius: '12px',
                ml: 1,
                height: 36, // match Sign In button height
                width: 36, // make it a square
              }}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>

            {/* Drawer for Mobile */}
            <Drawer
              anchor='right'
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
            >
              <List sx={{ width: 200 }}>
                {navItems.map((item) => (
                  <ListItem key={item.label} disablePadding>
                    <ListItemButton
                      component={NavLink}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      sx={{
                        color: AppColors.menuColor,
                        fontSize: 'clamp(0.8rem, 1.2vw, 1.2rem)',
                        '&.active': {
                          fontWeight: 'bold',
                        },
                      }}
                    >
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Drawer>
          </Box>
        </Toolbar>
      </AppBar>

      <BookDemoFormModel
        onClose={onCloseBookDemoForm}
        toggle={toggleBookDemoForm}
        onSubmitSuccess={() => {}}
      />
    </>
  );
};

export default Header;
